import { ref, reactive } from 'vue';
import { fetchHistoryGrouped, fetchHistoryList } from '@/api/historyApi';

export function useHistoryList() {
  const loading = ref(true);
  const loadedOnce = ref(false);
  const error = ref('');
  const flatRows = ref([]);
  const groupedGroups = ref([]);
  const total = ref(0);
  const viewMode = ref('flat');
  const filters = reactive({
    search: '',
    refType: '',
    dateFrom: '',
    dateTo: ''
  });

  let loadSeq = 0;

  function buildApiFilter() {
    const filter = {};
    if (filters.search.trim()) filter.search = filters.search.trim();
    if (filters.refType) filter.refType = filters.refType;
    if (filters.dateFrom) filter.dateFrom = filters.dateFrom;
    if (filters.dateTo) filter.dateTo = filters.dateTo;
    return filter;
  }

  async function loadList() {
    const seq = ++loadSeq;
    loading.value = true;
    error.value = '';
    try {
      const filter = buildApiFilter();
      if (viewMode.value === 'grouped') {
        const { data } = await fetchHistoryGrouped({ filter, limit: 100 });
        if (seq !== loadSeq) return;
        groupedGroups.value = data.groups || [];
        flatRows.value = [];
        total.value = data.total ?? groupedGroups.value.length;
      } else {
        const { data } = await fetchHistoryList({ filter, limit: 500 });
        if (seq !== loadSeq) return;
        flatRows.value = data.rows || [];
        groupedGroups.value = [];
        total.value = data.total ?? flatRows.value.length;
      }
    } catch (err) {
      if (seq !== loadSeq) return;
      error.value = err.message || 'Failed to load history.';
      flatRows.value = [];
      groupedGroups.value = [];
      total.value = 0;
    } finally {
      if (seq === loadSeq) {
        loading.value = false;
        loadedOnce.value = true;
      }
    }
  }

  function reload() {
    return loadList();
  }

  function clearFilters() {
    filters.search = '';
    filters.refType = '';
    filters.dateFrom = '';
    filters.dateTo = '';
    return reload();
  }

  function setViewMode(mode) {
    const next = mode === 'grouped' ? 'grouped' : 'flat';
    if (viewMode.value === next) return reload();
    viewMode.value = next;
    return reload();
  }

  return {
    loading,
    loadedOnce,
    error,
    flatRows,
    groupedGroups,
    total,
    viewMode,
    filters,
    loadList,
    reload,
    clearFilters,
    setViewMode,
    buildApiFilter
  };
}
